import React from "react";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import PendingDataNotice from "./PendingDataNotice";
import Tooltip from "./Tooltip";
import "./styles/spinner.css";

const getIncidents = (data) => {
    if (Array.isArray(data)) return data;
    return data?.incidents || [];
};

const getSummary = (data, incidents) => {
    if (!Array.isArray(data) && data?.summary) {
        return data.summary;
    }
    return {
        total_incidents: incidents.length,
        owners_involved: new Set(incidents.map((incident) => incident.team)).size,
        inactive_starters: incidents.filter(
            (incident) => incident.active_status === "inactive"
        ).length,
    };
};

const getSeasonLeaderboard = (data) => {
    if (Array.isArray(data)) return [];
    return data?.season_leaderboard || [];
};

const formatStatus = (status) => {
    if (!status) return "Flagged";
    if (status === "inactive/bye") return "Inactive/Bye";
    return status.charAt(0).toUpperCase() + status.slice(1);
};

const statusPillClasses = (status) => {
    if (status === "inactive") {
        return "bg-[rgba(197,109,78,0.16)] text-[#8f492f]";
    }
    return "bg-[rgba(211,166,74,0.18)] text-[#7a5e15]";
};

const incidentDescription = (entry) => {
    if (entry.active_status === "inactive") {
        return "Inactive player left in an active lineup slot for the week.";
    }
    if (entry.active_status === "bye") {
        return "Bye-week player left in the lineup through kickoff.";
    }
    if (entry.active_status === "inactive/bye") {
        return "Historical season data tracks the total number of inactive or bye starters for this owner.";
    }
    return "Flagged starter left in the lineup for this week.";
};

const contextTooltipLabel = (entry) => {
    switch (entry.context) {
    case "Would have won":
        return "Context: Swapping this starter for the best eligible bench option would have changed the matchup to a win.";
    case "Would have tied":
        return "Context: Swapping this starter for the best eligible bench option would have changed the matchup to a tie.";
    case "Would have lost anyway":
        return "Context: Even the best eligible bench replacement would not have changed the loss.";
    case "Won anyway":
        return "Context: This starter did not cost the matchup because the team still won.";
    case "Tied anyway":
        return "Context: This starter did not swing the result because the matchup still ended in a tie.";
    case "Bench may have won it":
        return "Context: Historical data suggests the bench had enough points to flip the result, but the exact slot-eligible swap is not available.";
    case "Bench may have tied it":
        return "Context: Historical data suggests the bench had enough points to force a tie, but the exact slot-eligible swap is not available.";
    case "Bench may not have saved it":
        return "Context: Historical data suggests the available bench points still would not have changed the result.";
    default:
        return "Context: Shows how much the flagged starter appears to have mattered to the matchup result.";
    }
};

const NaughtyList = ({ data, week, loading, nCompletedWeeks }) => {
    const incidents = getIncidents(data);
    const summary = getSummary(data, incidents);
    const seasonLeaderboard = getSeasonLeaderboard(data);
    const repeatOffenders = new Set(
        seasonLeaderboard
            .filter((row) => row.weeks_with_offense > 1)
            .map((row) => row.owner)
    );

    return (
        <CopyableContainer
            title={
                <LeagueTabHeading
                    eyebrow="League discipline report"
                    title="Naughty List"
                    className="mb-0"
                />
            }
            fileName={`naughty-list-week-${week}`}
            style={{ maxWidth: "none" }}
        >
            <PendingDataNotice
                dataType="Naughty List"
                isPending={week > nCompletedWeeks}
            />

            {loading || !data ? (
                <div className="spinner-container">
                    <div className="spinner"></div>
                    <p>Loading Naughty List...</p>
                </div>
            ) : (
                <div className="grid w-full items-start gap-5 lg:grid-cols-[minmax(0,1.7fr)_minmax(430px,1fr)] xl:grid-cols-[minmax(0,1.55fr)_minmax(470px,1fr)]">
                    <section className="rounded-[24px] border border-[rgba(38,74,59,0.12)] bg-[rgba(255,253,249,0.96)] p-5 shadow-[0_10px_28px_rgba(57,64,42,0.06)]">
                        <div className="mb-4 grid gap-3 md:grid-cols-3">
                            <div className="rounded-[18px] border border-[rgba(38,74,59,0.12)] bg-[#faf6ee] p-4">
                                <span className="mb-2 block text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#687168]">
                                    Total incidents
                                </span>
                                <strong className="text-[28px] text-[#264a3b]">
                                    {summary.total_incidents ?? 0}
                                </strong>
                            </div>
                            <div className="rounded-[18px] border border-[rgba(38,74,59,0.12)] bg-[#faf6ee] p-4">
                                <span className="mb-2 block text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#687168]">
                                    Owners involved
                                </span>
                                <strong className="text-[28px] text-[#264a3b]">
                                    {summary.owners_involved ?? 0}
                                </strong>
                            </div>
                            <div className="rounded-[18px] border border-[rgba(38,74,59,0.12)] bg-[#faf6ee] p-4">
                                <span className="mb-2 block text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#687168]">
                                    Inactive starters
                                </span>
                                <strong className="text-[28px] text-[#264a3b]">
                                    {summary.inactive_starters ?? 0}
                                </strong>
                            </div>
                        </div>

                        {incidents.length === 0 ? (
                            <div className="rounded-[20px] border border-[rgba(78,138,130,0.16)] bg-[linear-gradient(180deg,rgba(78,138,130,0.12),rgba(78,138,130,0.04))] p-5">
                                <strong className="mb-2 block text-[18px] text-[#2d6661]">
                                    No teams started any inactive players.
                                </strong>
                                <p className="m-0 text-[14px] leading-6 text-[#687168]">
                                    A rare clean week. The season leaderboard still
                                    tracks who has shown up here most often.
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {incidents.map((entry, index) => (
                                    <article
                                        key={`${entry.team}-${entry.player}-${index}`}
                                        className="grid gap-3 rounded-[20px] border border-[rgba(197,109,78,0.14)] bg-[linear-gradient(180deg,rgba(197,109,78,0.1),rgba(197,109,78,0.04))] p-3 lg:grid-cols-[110px_minmax(0,1fr)] lg:items-center"
                                    >
                                        <div className="lg:pr-3">
                                            <div className="text-[13px] font-extrabold text-[#264a3b]">
                                                {entry.team}
                                            </div>
                                            {repeatOffenders.has(entry.team) ? (
                                                <div className="mt-1 inline-flex items-center justify-center rounded-full border border-[rgba(143,73,47,0.18)] bg-[rgba(197,109,78,0.12)] px-1.5 py-[0.2rem] text-center text-[9px] font-extrabold uppercase tracking-[0.06em] text-[#8f492f]">
                                                    Repeat offender
                                                </div>
                                            ) : null}
                                        </div>
                                        <div className="lg:pl-2">
                                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                                <strong className="text-[18px] text-[#213028]">
                                                    {entry.player}
                                                </strong>
                                                <Tooltip
                                                    label={`${formatStatus(entry.active_status)}: ${incidentDescription(entry)}`}
                                                    wrapperClassName="inline-flex"
                                                >
                                                    <div
                                                        className={`inline-flex h-fit items-center rounded-full px-2 py-[0.3rem] text-[11px] font-extrabold uppercase tracking-[0.08em] ${statusPillClasses(entry.active_status)}`}
                                                    >
                                                        {formatStatus(entry.active_status)}
                                                    </div>
                                                </Tooltip>
                                            </div>
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {entry.position ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(38,74,59,0.1)] bg-[rgba(255,253,249,0.72)] px-2 py-[0.3rem] text-[11px] font-bold tracking-[0.04em] text-[#687168]">
                                                        Position
                                                        <strong className="text-[#264a3b]">
                                                            {entry.position}
                                                        </strong>
                                                    </span>
                                                ) : null}
                                                {entry.context ? (
                                                    <Tooltip
                                                        label={contextTooltipLabel(entry)}
                                                        position="top"
                                                        wrapperClassName="inline-flex"
                                                    >
                                                        <span className="inline-flex items-center gap-1 rounded-full border border-[rgba(38,74,59,0.1)] bg-[rgba(255,253,249,0.72)] px-2 py-[0.3rem] text-[11px] font-bold tracking-[0.04em] text-[#687168]">
                                                            Context
                                                            <strong className="text-[#264a3b]">
                                                                {entry.context}
                                                            </strong>
                                                        </span>
                                                    </Tooltip>
                                                ) : null}
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>

                    <aside className="grid gap-4">
                        <div className="rounded-[20px] border border-[rgba(38,74,59,0.12)] bg-[linear-gradient(180deg,rgba(115,131,111,0.12),rgba(115,131,111,0.04))] p-4">
                            <div className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#687168]">
                                Season-long leaderboard
                            </div>
                            <h3 className="mb-3 text-[18px] font-extrabold text-[#213028]">
                                Who shows up here the most
                            </h3>
                            {seasonLeaderboard.length === 0 ? (
                                <p className="m-0 text-[14px] leading-6 text-[#687168]">
                                    No season-long offenses have been recorded yet.
                                </p>
                            ) : (
                                <div className="grid gap-2.5">
                                    <div className="grid grid-cols-[minmax(0,1fr)_78px_88px] gap-2 px-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#687168]">
                                        <span>Owner</span>
                                        <span className="text-center"># Weeks</span>
                                        <span className="text-center"># Offences</span>
                                    </div>
                                    {seasonLeaderboard.slice(0, 5).map((row) => (
                                        <div
                                            key={row.team_id || row.owner}
                                            className="grid grid-cols-[minmax(0,1fr)_78px_88px] items-center gap-2 rounded-2xl border border-[rgba(38,74,59,0.12)] bg-[#fffaf2] px-3.5 py-3 text-[14px]"
                                        >
                                            <strong className="leading-5 text-[#264a3b]">
                                                {row.owner}
                                            </strong>
                                            <span className="text-center font-extrabold text-[#264a3b]">
                                                {row.weeks_with_offense}
                                            </span>
                                            <span className="text-center font-extrabold text-[#264a3b]">
                                                {row.offenses}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="mb-0 mt-3 text-[13px] leading-6 text-[#687168]">
                                Weeks counts how often an owner appeared on the tab.
                                Offenses counts the total number of flagged starters
                                across the season.
                            </p>
                        </div>
                    </aside>
                </div>
            )}
        </CopyableContainer>
    );
};

export default NaughtyList;
